import React from 'react';
import type {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import {
  Badge,
  Callout,
  Code,
  Flex,
  FormField,
  Icon,
  Strong,
  Tabs,
  Text,
  TextField,
} from '@scale/scaleui-radix';
import ChatApiEndpointsBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ChatApiEndpointsBuilder';
import type { ChatApiEndpointWithId, ProjectChatApiEndpointWithId } from '@scale/shared/types/chat';
import useSweapPassAtKCard from './useSweapPassAtKCard';
import { CompassInstructions } from '@scale/compass-ui/components/CardInstructions';
import {
  faBookOpen,
  faCircleExclamation,
  faInfoCircle,
  faPlay,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons';
import { SweapRunMode } from '@scale/genai-compass-shared/cards/sweap_pass_at_k';

const REQUIRED_COLUMNS = [
  'image_name',
  'problem_statement',
  'instance_id',
  'base_commit',
  'repo_name',
] as const;

const K_MIN = 1;
const K_MAX = 20;

const SweapPassAtKCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { context, setRunMode, setK, setConfigS3Uri, setModelConfig, setBatchId } =
    useSweapPassAtKCard(index);

  const runMode = context.runMode || SweapRunMode.NEW_RUN;
  const isNewRun = runMode === SweapRunMode.NEW_RUN;
  const modelConfigValue = context.modelConfig ? [context.modelConfig] : [];
  const kValue = Number.isFinite(context.k) ? (context.k as number) : 3;

  const validationIssues: string[] = [];

  // Batch ID is always required
  if (!context.batchId?.trim() || !/^[a-zA-Z0-9_-]+$/.test(context.batchId.trim())) {
    validationIssues.push(
      'Batch ID is required and must contain only letters, numbers, hyphens, and underscores.',
    );
  }

  // Model config, K, and configS3Uri are only required for new runs
  if (isNewRun) {
    if (!context.modelConfig) {
      validationIssues.push('Select exactly one model endpoint config.');
    }
    if (!Number.isFinite(kValue) || kValue < K_MIN || kValue > K_MAX) {
      validationIssues.push(`K must be an integer between ${K_MIN} and ${K_MAX}.`);
    }
    if (!context.configS3Uri) {
      validationIssues.push('Provide an SWE agent config S3 URI (e.g. s3://.../tool_use.yaml).');
    } else if (!context.configS3Uri.trim().startsWith('s3://')) {
      validationIssues.push('SWE agent config must be an S3 URI starting with "s3://".');
    }
  }

  return (
    <div className="flex flex-col gap-3 p-2">
      <div className="mb-1">
        <CompassInstructions
          header="How to use the SWEAP Pass@K card"
          storageKey="sweapPassAtKCardInstructionsExpanded"
          icon={faBookOpen}
        >
          <Callout.Text>
            <Text as="p" size="2">
              This card runs SWEAP auto-evaluation and computes Pass@K metrics per SWE-bench
              <Code>instance_id</Code>.
            </Text>

            <Text as="p" size="2" className="mt-2">
              <Strong>New Run:</Strong> Start a fresh SWEAP evaluation with input data, model
              config, and parameters.
            </Text>

            <Text as="p" size="2" className="mt-2">
              <Strong>Fetch Existing Run:</Strong> Resume or fetch results from a previously started
              run using its Batch ID. Useful for recovering from interruptions or re-fetching
              results.
            </Text>

            <Text as="p" size="2" className="mt-2">
              <Strong>Output:</Strong> A table with one row per <Code>instance_id</Code>, columns{' '}
              <Code>run1..runK</Code> (per-attempt pass/fail), <Code>pass_count</Code>, and{' '}
              <Code>pass@k</Code>.
            </Text>
          </Callout.Text>
        </CompassInstructions>
      </div>

      {validationIssues.length > 0 && (
        <Callout.Root color="red" variant="soft" role="alert" size="2" className="items-start">
          <Callout.Icon>
            <Icon icon={faCircleExclamation} label="Error" />
          </Callout.Icon>
          <Callout.Text>
            <Text as="p" size="2">
              <Strong>Please fix the following before running:</Strong>
            </Text>
            <ul className="mt-1 list-disc pl-5">
              {validationIssues.map(issue => (
                <li key={issue}>
                  <Text as="span" size="2">
                    {issue}
                  </Text>
                </li>
              ))}
            </ul>
          </Callout.Text>
        </Callout.Root>
      )}

      <Tabs.Root
        value={runMode}
        onValueChange={value => setRunMode(value as SweapRunMode)}
        className="w-full"
      >
        <Tabs.List>
          <Tabs.Trigger value={SweapRunMode.NEW_RUN}>
            <Icon icon={faPlay} label="New Run" className="mr-1" />
            New Run
          </Tabs.Trigger>
          <Tabs.Trigger value={SweapRunMode.FETCH_EXISTING}>
            <Icon icon={faSearch} label="Fetch Existing" className="mr-1" />
            Fetch Existing Run
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value={SweapRunMode.NEW_RUN} className="pt-3">
          <div className="flex flex-col gap-3">
            <Callout.Root color="neutral" variant="soft" size="2" className="items-start">
              <Callout.Icon>
                <Icon icon={faInfoCircle} label="Info" />
              </Callout.Icon>
              <Callout.Text>
                <Text as="p" size="2">
                  <Strong>Input CSV requirements</Strong>
                </Text>
                <Flex wrap="wrap" gap="1" className="mt-2">
                  {REQUIRED_COLUMNS.map(col => (
                    <Badge key={col} variant="soft" color="blue">
                      {col}
                    </Badge>
                  ))}
                </Flex>
              </Callout.Text>
            </Callout.Root>

            <FormField.Root>
              <FormField.Label>Batch ID</FormField.Label>
              <TextField.Root
                value={context.batchId || ''}
                placeholder="e.g. my-sweap-run-2026-02-24"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBatchId(e.target.value)}
              />
              <FormField.Description>
                Unique identifier for this run. Used to tag Temporal workflows and enables resuming
                if interrupted.
              </FormField.Description>
            </FormField.Root>

            <ChatApiEndpointsBuilder
              name="Model Configuration"
              description="Select exactly one model endpoint config (URL + params)."
              existingValue={
                modelConfigValue as Array<ChatApiEndpointWithId | ProjectChatApiEndpointWithId>
              }
              required={true}
              updateProperty={(
                value: Array<ChatApiEndpointWithId | ProjectChatApiEndpointWithId>,
              ) => {
                const first = value?.[0] as ChatApiEndpointWithId | undefined;
                setModelConfig(first);
              }}
              maxNumberOfEndpoints={1}
              showTestButton={false}
            />

            <FormField.Root>
              <FormField.Label>K (Pass@K)</FormField.Label>
              <TextField.Root
                type="number"
                inputMode="numeric"
                min={K_MIN}
                max={K_MAX}
                step={1}
                value={String(kValue)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const raw = e.target.value;
                  const next = parseInt(raw, 10);
                  if (!Number.isFinite(next)) {
                    return;
                  }
                  setK(Math.max(K_MIN, Math.min(K_MAX, next)));
                }}
              />
              <FormField.Description>
                Number of independent solution attempts per problem. Higher K is slower but more
                informative.
              </FormField.Description>
            </FormField.Root>

            <FormField.Root>
              <FormField.Label>SWE agent config (S3 URI)</FormField.Label>
              <TextField.Root
                value={context.configS3Uri || ''}
                placeholder="s3://scale-dev/model-eval/auto-evaluation/swe-agent/config/tool_use.yaml"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConfigS3Uri(e.target.value)
                }
              />
              <FormField.Description>
                Path to the SWE agent config YAML in S3 (default <Strong>tool_use.yaml</Strong>). It
                defines the agent's prompts/templates and available tools/commands.
              </FormField.Description>
            </FormField.Root>
          </div>
        </Tabs.Content>

        <Tabs.Content value={SweapRunMode.FETCH_EXISTING} className="pt-3">
          <div className="flex flex-col gap-3">
            <Callout.Root color="blue" variant="soft" size="2" className="items-start">
              <Callout.Icon>
                <Icon icon={faInfoCircle} label="Info" />
              </Callout.Icon>
              <Callout.Text>
                <Text as="p" size="2">
                  Enter the Batch ID of an existing SWEAP run to fetch its status and results. If
                  the run is still in progress, this will wait for completion. If already completed,
                  results will be returned immediately.
                </Text>
              </Callout.Text>
            </Callout.Root>

            <FormField.Root>
              <FormField.Label>Batch ID</FormField.Label>
              <TextField.Root
                value={context.batchId || ''}
                placeholder="e.g. my-sweap-run-2026-02-24"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBatchId(e.target.value)}
              />
              <FormField.Description>
                The Batch ID of the existing SWEAP run you want to fetch results from.
              </FormField.Description>
            </FormField.Root>
          </div>
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
};

export default SweapPassAtKCardComponent;
